import React, { useId } from 'react'

/**
 * Isometric diagram of the total rewards stack.
 *
 * Geometry is computed from a true 2:1 isometric projection rather than
 * eyeballed polygons, so the slab edges actually line up and the whole thing
 * reads as one solid object. Each slab is drawn as three faces (top, left,
 * right) with its own shading, which is what sells the extrusion.
 */

// 2:1 isometric — 30deg from horizontal.
const COS30 = Math.cos(Math.PI / 6)
const SIN30 = Math.sin(Math.PI / 6)

function project(x, y, z) {
  return [(x - y) * COS30, (x + y) * SIN30 - z]
}

function pts(list) {
  return list.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
}

/** One slab: a half-extent square plan of `size`, `depth` thick, floating at `z`. */
function Slab({ size, depth, z, palette, label, value, labelSide = 'right' }) {
  const s = size

  // Top face corners, clockwise from the far corner.
  const topFar = project(-s, -s, z)
  const topRight = project(s, -s, z)
  const topNear = project(s, s, z)
  const topLeft = project(-s, s, z)

  // Matching corners on the underside.
  const botRight = project(s, -s, z - depth)
  const botNear = project(s, s, z - depth)
  const botLeft = project(-s, s, z - depth)

  // Anchor for the leader line, out past the near-right edge.
  const anchor = project(s, 0, z - depth / 2)
  const outward = labelSide === 'right' ? 1 : -1
  const tipX = anchor[0] + outward * 46
  const tipY = anchor[1] + 2

  return (
    <g>
      {/* right face — most shadowed */}
      <polygon points={pts([topRight, topNear, botNear, botRight])} fill={palette.right} />
      {/* left/front face — mid tone */}
      <polygon points={pts([topNear, topLeft, botLeft, botNear])} fill={palette.left} />
      {/* top face — catches the light */}
      <polygon points={pts([topFar, topRight, topNear, topLeft])} fill={palette.top} />
      {/* lit edge along the two far top edges */}
      <polyline
        points={pts([topLeft, topFar, topRight])}
        fill="none"
        stroke={palette.edge}
        strokeWidth="1.1"
        strokeLinecap="round"
        opacity="0.85"
      />

      {/* leader line + label */}
      <line
        x1={anchor[0]}
        y1={anchor[1]}
        x2={tipX}
        y2={tipY}
        stroke="rgba(255,209,24,0.45)"
        strokeWidth="1"
      />
      <circle cx={anchor[0]} cy={anchor[1]} r="2.4" fill="#ffd118" />
      <text
        x={tipX + outward * 8}
        y={tipY - 3}
        textAnchor={labelSide === 'right' ? 'start' : 'end'}
        fill="#ffffff"
        fontSize="12.5"
        fontWeight="600"
        fontFamily="Open Sans, system-ui, sans-serif"
      >
        {label}
      </text>
      <text
        x={tipX + outward * 8}
        y={tipY + 13}
        textAnchor={labelSide === 'right' ? 'start' : 'end'}
        fill="rgba(255,255,255,0.55)"
        fontSize="10.5"
        fontFamily="Open Sans, system-ui, sans-serif"
      >
        {value}
      </text>
    </g>
  )
}

const LAYERS = [
  {
    label: 'Base pay',
    value: 'Structures, ranges, job architecture',
    size: 96,
    depth: 15,
    z: 0,
    palette: { top: '#2a2a2a', left: '#171717', right: '#0d0d0d', edge: 'rgba(255,255,255,0.3)' }
  },
  {
    label: 'Benefits',
    value: 'Health, retirement, family, mobility',
    size: 80,
    depth: 14,
    z: 34,
    palette: { top: '#3a3320', left: '#221e13', right: '#14110a', edge: 'rgba(255,209,24,0.42)' }
  },
  {
    label: 'Variable pay',
    value: 'STI, commissions, merit matrices',
    size: 64,
    depth: 13,
    z: 66,
    palette: { top: '#6b5610', left: '#40330a', right: '#2a2106', edge: 'rgba(255,209,24,0.6)' }
  },
  {
    label: 'Equity',
    value: 'Options, RSUs, ESOPs, cliffs',
    size: 46,
    depth: 12,
    z: 96,
    palette: { top: '#ffd118', left: '#bf9f02', right: '#8c7401', edge: 'rgba(255,249,214,0.95)' }
  }
]

export default function RewardsArchitecture({ className = '' }) {
  const glow = useId()

  return (
    <svg
      viewBox="-210 -150 420 330"
      className={className}
      role="img"
      aria-label="Isometric diagram of the total rewards stack: base pay at the foundation, then benefits, variable pay, and equity at the top."
    >
      <defs>
        <radialGradient id={glow} cx="50%" cy="42%" r="52%">
          <stop offset="0%" stopColor="#ffd118" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#ffd118" stopOpacity="0" />
        </radialGradient>
      </defs>

      <ellipse cx="0" cy="20" rx="200" ry="130" fill={`url(#${glow})`} />

      {/* Contact shadow, so the stack sits on something. */}
      <ellipse cx="0" cy="104" rx="118" ry="34" fill="rgba(0,0,0,0.55)" />

      {/* Painter's algorithm: lowest slab first. */}
      {LAYERS.map((layer, i) => (
        <Slab key={layer.label} {...layer} labelSide={i % 2 === 0 ? 'right' : 'left'} />
      ))}
    </svg>
  )
}
